import { useState } from "react";
import type { QaGitHubRepo, QaGitHubRepoInput, QaRunOptions } from "../../api/qaOrchestrator";
import { splitList } from "./qaShared";

// Every option of one repository entry except where it runs, which is edited
// right in its row.

type Form = {
  scope: string;
  plans: string;
  report: "status" | "check";
  context: string;
  comment: boolean;
  allowForks: boolean;
  detailsUrl: string;
  notes: string;
  port: string;
  maxRetries: string;
  recordStartup: boolean;
  setupChecks: string;
};

const toForm = (r: QaGitHubRepo): Form => ({
  scope: r.scope,
  plans: (r.plans ?? []).join(", "),
  report: r.report,
  context: r.context,
  comment: r.comment,
  allowForks: r.allowForks,
  detailsUrl: r.detailsUrl ?? "",
  notes: r.notes ?? "",
  port: String(r.options?.port ?? 19132),
  maxRetries: r.options?.maxRetries === undefined ? "" : String(r.options.maxRetries),
  recordStartup: !!r.options?.recordStartup,
  setupChecks: r.options?.setupChecks?.length ? JSON.stringify(r.options.setupChecks, null, 2) : "",
});

/** The form as a PATCH body, or the first thing wrong with it. */
export function repoPatch(f: Form): Partial<QaGitHubRepoInput> | string {
  if (!/^\d+$/.test(f.port)) return "Port must be a number";
  if (f.maxRetries && !/^[0-5]$/.test(f.maxRetries)) return "Retries must be 0 to 5";
  if (!f.context.trim()) return "The status name can't be empty";
  let setupChecks: Record<string, unknown>[] | undefined;
  if (f.setupChecks.trim()) {
    try {
      setupChecks = JSON.parse(f.setupChecks);
    } catch {
      return "Setup checks must be JSON";
    }
    if (!Array.isArray(setupChecks)) return "Setup checks must be a JSON list";
  }
  const options: QaRunOptions = {
    port: Number(f.port),
    ...(f.maxRetries ? { maxRetries: Number(f.maxRetries) } : {}),
    ...(f.recordStartup ? { recordStartup: true } : {}),
    ...(setupChecks?.length ? { setupChecks } : {}),
  };
  const plans = splitList(f.plans);
  return {
    scope: f.scope.trim(),
    plans: plans.length ? plans : null,
    report: f.report,
    context: f.context.trim(),
    comment: f.comment,
    allowForks: f.allowForks,
    detailsUrl: f.detailsUrl.trim() || null,
    notes: f.notes.trim() || null,
    options,
  };
}

export default function QaRepoSettings({ repo, busy, onSave, onCancel }: { repo: QaGitHubRepo; busy: boolean; onSave: (patch: Partial<QaGitHubRepoInput>) => void; onCancel: () => void }) {
  const [f, setF] = useState<Form>(() => toForm(repo));
  const patch = repoPatch(f);
  const set = <K extends keyof Form>(k: K) => (e: { target: { value: string; checked?: boolean } }) =>
    setF({ ...f, [k]: typeof f[k] === "boolean" ? !!e.target.checked : e.target.value });

  return (
    <div className="qo-form qo-repo-settings">
      <div className="qo-grid">
        <label>Scope <small>owner/repo, owner or *</small><input value={f.scope} onChange={set("scope")} /></label>
        <label>Plans <small>comma separated; empty runs every plan in qa/plans/</small><input value={f.plans} onChange={set("plans")} placeholder="all plans" /></label>
        <label>
          Report as <small>check runs need a GitHub App token</small>
          <select value={f.report} onChange={set("report")}>
            <option value="status">Commit status</option>
            <option value="check">Check run</option>
          </select>
        </label>
        <label>Status name <small>the status context or check run name</small><input value={f.context} onChange={set("context")} /></label>
        <label>Run page link <small>{"{testRun}"} becomes the run's id</small><input value={f.detailsUrl} onChange={set("detailsUrl")} placeholder="https://…/qa/project?run={testRun}" /></label>
        <label>Notes<input value={f.notes} onChange={set("notes")} /></label>
      </div>
      <div className="qo-checks">
        <label><input type="checkbox" checked={f.comment} onChange={set("comment")} /> One comment per pull request with the results</label>
        <label><input type="checkbox" checked={f.allowForks} onChange={set("allowForks")} /> Test pull requests from forks <small className="qo-muted">(their plans run on your runners)</small></label>
      </div>
      <div className="qo-grid">
        <label>Minecraft server port<input value={f.port} onChange={set("port")} inputMode="numeric" /></label>
        <label>Retries <small>0–5, after an environment failure</small><input value={f.maxRetries} onChange={set("maxRetries")} inputMode="numeric" placeholder="runner default" /></label>
      </div>
      <div className="qo-checks">
        <label><input type="checkbox" checked={f.recordStartup} onChange={set("recordStartup")} /> Record the game starting up too</label>
      </div>
      <label className="qo-field">Setup checks <small>JSON list of screenshot checks on the runner's own setup (menu, joined world); a mismatch stops the run as a setup error</small>
        <textarea rows={3} value={f.setupChecks} onChange={set("setupChecks")} spellCheck={false} />
      </label>
      <div className="qo-actions">
        <button className="btn btn-accent" disabled={busy || typeof patch === "string"} onClick={() => typeof patch !== "string" && onSave(patch)}>Save</button>
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
        {typeof patch === "string" && <span className="qo-error-text">{patch}</span>}
      </div>
    </div>
  );
}
